import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { motion } from "framer-motion";

export default function PeoplePage() {
  const [people, setPeople] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const fetchPeople = async () => {
    try {
      const res = await API.get("/people");
      setPeople(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchPeople();
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    try {
      if (editing) {
        await API.put(`/people/${editing}`, { name, email, phone });
        toast.success("Person updated");
      } else {
        await API.post("/people", { name, email, phone });
        toast.success("Person added");
      }
      resetForm();
      fetchPeople();
    } catch {
      toast.error("Failed");
    }
  };

  const handleEdit = (person) => {
    setEditing(person.person_id);
    setName(person.name);
    setEmail(person.email || "");
    setPhone(person.phone || "");
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Delete Person", "This will remove this person from your contacts."))) return;
    await API.delete(`/people/${id}`);
    toast.success("Person removed");
    fetchPeople();
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setName("");
    setEmail("");
    setPhone("");
  };

  return (
    <div data-testid="people-page" className="max-w-[900px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-[28px] font-bold text-[#37352F]">
            People
          </h1>
          <p className="text-sm text-[#787774] mt-1">
            Manage your contacts for delegation
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white"
          data-testid="add-person-button"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Person
        </Button>
      </div>

      {people.length === 0 ? (
        <div className="text-center py-16">
          <Users
            className="w-10 h-10 text-[#E0E0DE] mx-auto mb-3"
            strokeWidth={1}
          />
          <p className="text-[#787774] italic">No people added yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {people.map((person, i) => (
            <motion.div
              key={person.person_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="flex items-center justify-between p-4 border border-[#E0E0DE] rounded-sm"
              data-testid={`person-item-${person.person_id}`}
            >
              <div>
                <h3 className="text-sm font-medium text-[#37352F]">
                  {person.name}
                </h3>
                <div className="flex gap-4 mt-0.5">
                  {person.email && (
                    <span className="text-xs text-[#787774]">
                      {person.email}
                    </span>
                  )}
                  {person.phone && (
                    <span className="text-xs text-[#787774]">
                      {person.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={() => handleEdit(person)}
                  variant="ghost"
                  size="icon"
                  className="text-[#787774]"
                  data-testid={`edit-person-${person.person_id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  onClick={() => handleDelete(person.person_id)}
                  variant="ghost"
                  size="icon"
                  className="text-[#EB5757]"
                  data-testid={`delete-person-${person.person_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog
        open={showForm}
        onOpenChange={(v) => {
          if (!v) resetForm();
          else setShowForm(true);
        }}
      >
        <DialogContent className="bg-[#FFFFFF] border-[#E0E0DE]">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#37352F]">
              {editing ? "Edit Person" : "Add Person"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">
                Name *
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 bg-transparent border-[#E0E0DE]"
                data-testid="person-name-input"
              />
            </div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">
                Email (optional)
              </Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-transparent border-[#E0E0DE]"
                data-testid="person-email-input"
              />
            </div>
            <div>
              <Label className="text-[12px] text-[#6B6B6B]">
                Phone (optional)
              </Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 bg-transparent border-[#E0E0DE]"
                data-testid="person-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSubmit}
              className="bg-[#2383E2] hover:bg-[#1B6EC2] text-white"
              data-testid="submit-person-button"
            >
              {editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
